import { Link } from 'react-router-dom'

import type { Message, MessageHighlight } from '../../api/types'
import { datumText } from '../../lib/ortszeit'
import { WetterKurz, WetterNennung, hatWetter } from '../map/Wetter'
import { zeichenFuer } from '../map/Umgebungswahl'

/**
 * Die Kärtchen unter einer Freigabe-Sprechblase.
 *
 * **Gezeichnet und kein Bild.** Ein hochgeladenes Abbild wäre auf jedem Schirm
 * unscharf, friere den Text ein — wer den Punkt später ändert, hätte im Chat
 * den alten Stand — und läge doppelt im Speicher. Und **keine Landkarte**: die
 * hieße, dass der Browser jedes Lesers Kacheln holt, also seine IP an den
 * Kartenserver geht. Für eine Sprechblase ist das der falsche Preis.
 *
 * **Höchstens fünf**, dann eine Zeile mit dem Rest. Eine Reihe zum Durchwischen
 * ist ein Blickfang, dreißig sind eine Wand.
 */
export function Highlightkarten({
  message,
  reiseVersatz,
}: {
  message: Message
  /** Die Uhr der Reise — sie gilt für jeden Punkt ohne eigene. */
  reiseVersatz: number | null
}) {
  const punkte = message.highlights
  if (punkte === null || punkte.items.length === 0) return null

  const weitere = punkte.total - punkte.items.length
  const erstes = punkte.items.find((h) => !h.gone)

  return (
    <div className="hlkarten">
      <div className="hlkarten__band">
        {punkte.items.map((punkt) => (
          <Kaertchen
            key={punkt.id}
            punkt={punkt}
            tripId={message.trip_id}
            reiseVersatz={reiseVersatz}
          />
        ))}
      </div>

      {/* **Der Rest als Zahl, nicht als Liste.** Es können sehr viele auf
          einmal freigegeben werden; eine Aufzählung wäre dann die Wand, die
          das Band gerade vermeidet. Der Verweis führt auf das erste — von dort
          wischt man weiter. */}
      {weitere > 0 && erstes !== undefined && (
        <p className="hlkarten__mehr">
          <Link to={`/t/${message.trip_id}/karte?punkt=${erstes.id}`}>
            {weitere === 1 ? 'und ein weiterer Punkt' : `und ${weitere} weitere Punkte`} auf der
            Karte
          </Link>
        </p>
      )}

      {/* Die Nennung, die WeatherKit verlangt — einmal je Sprechblase und nur,
          wenn ein Kärtchen wirklich Wetter zeigt. */}
      {punkte.items.some((h) => !h.gone && hatWetter(h.weather ?? null)) && (
        <p className="hlkarten__nennung">
          Wetter: <WetterNennung className="hlkarten__quelle" />
        </p>
      )}
    </div>
  )
}

function Kaertchen({
  punkt,
  tripId,
  reiseVersatz,
}: {
  punkt: MessageHighlight
  tripId: number
  reiseVersatz: number | null
}) {
  // **Zurückgezogen: nur der Titel.** Kein Foto, kein Ort, kein Wetter — und
  // kein Weg dorthin, denn dort ist nichts mehr. Die Sprechblase bleibt
  // trotzdem lesbar: der Titel war diesen Leuten gegenüber schon einmal
  // veröffentlicht, und ein Beitrag, der auf nichts zeigt, wäre ein Rätsel.
  if (punkt.gone) {
    return (
      <div className="hlkarte hlkarte--weg">
        <span className="hlkarte__titel">{punkt.title}</span>
        <span className="hlkarte__zeile">nicht mehr auf der Karte</span>
      </div>
    )
  }

  const wann =
    punkt.happened_at == null
      ? null
      : datumText(punkt.happened_at, punkt.tz_offset_min ?? reiseVersatz)
  const ort =
    punkt.place_name !== undefined && punkt.place_name !== '' && punkt.place_name !== punkt.title
      ? punkt.place_name
      : null

  return (
    <Link className="hlkarte" to={`/t/${tripId}/karte?punkt=${punkt.id}`}>
      {punkt.image_url == null ? (
        <span className="hlkarte__leer" aria-hidden="true" />
      ) : (
        <img className="hlkarte__bild" src={punkt.image_url} alt="" loading="lazy" />
      )}
      <span className="hlkarte__verlauf" aria-hidden="true" />
      <span className="hlkarte__inhalt">
        <span className="hlkarte__titel">
          {/* Dasselbe Zeichen wie im Kartenstapel: ein Punkt sieht an beiden
              Orten gleich aus, und es steht nur an Punkten aus der
              Umgebungssuche. */}
          {(() => {
            const Zeichen = zeichenFuer(punkt.poi_category ?? '')
            return Zeichen === null ? null : <Zeichen size={14} className="stapel__zeichen" />
          })()}
          {punkt.title}
        </span>
        <span className="hlkarte__zeile">{[ort, wann].filter(Boolean).join(' · ')}</span>
        <WetterKurz wetter={punkt.weather ?? null} />
      </span>
    </Link>
  )
}
